import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { showWarning } from 'src/utils/toast/toastUtil';
import { defaultHRM2130_R01Dto, HRM2130_R01Dto } from './Dto_HRM2130_R01';

/** 서버 목록·건수·Excel 조회에 공통으로 전달하는 화면 검색 조건이다. */
export interface HRM2130_R01Filter {
    attendGroup: string;
    useYn: string;
    attendNm: string;
}
/** 빈 문자열은 해당 검색 조건을 적용하지 않는 기본 상태를 뜻한다. */
export const defaultHRM2130_R01Filter: HRM2130_R01Filter = { attendGroup: '', useYn: '', attendNm: '' };

/** 행, 선택·삭제 표시, 검색 조건 및 그리드 표시 상태를 관리한다. */
export interface Store_HRM2130_R01 {
    HRM2130_R01Filter: HRM2130_R01Filter;
    HRM2130_R01PastFilter: HRM2130_R01Filter;
    setHRM2130_R01Filter: (filter: Partial<HRM2130_R01Filter>) => void;
    setHRM2130_R01PastFilter: (filter: Partial<HRM2130_R01Filter>) => void;
    HRM2130_R01List: HRM2130_R01Dto[];
    setHRM2130_R01List: (list: HRM2130_R01Dto[]) => void;
    insertHRM2130_R01List: (data: HRM2130_R01Dto[], index?: number) => void;
    removeHRM2130_R01List: (index: number) => void;
    updateHRM2130_R01List: (index: number, data: HRM2130_R01Dto) => void;
    selectedHRM2130_R01List: string[];
    setSelectedHRM2130_R01List: (ids: string[]) => void;
    addSelectedHRM2130_R01List: (ids: string[]) => void;
    removeSelectedHRM2130_R01List: (ids?: string[]) => void;
    initSelectedHRM2130_R01List: () => void;
    allSelectedHRM2130_R01List: () => void;
    deletedHRM2130_R01List: string[];
    addDeletedHRM2130_R01List: (ids: string[]) => void;
    removeDeletedHRM2130_R01List: (ids?: string[]) => void;
    originalHRM2130_R01List: HRM2130_R01Dto[];
    setOriginalHRM2130_R01List: (list: HRM2130_R01Dto[]) => void;
    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;
    scrollTop: number | null;
    setScrollTop: (top: number | null) => void;
    allCount: number;
    setAllCount: (count: number) => void;
    initData: () => void;
}

/**
 * HRM2130_R01 화면 전용 Store이다. 각 action은 반환값 없이 지정한 상태만 갱신한다.
 * 조회·저장 API 호출은 Content에서 수행한다.
 */
export const useHRM2130_R01Store = create<Store_HRM2130_R01>((set) => ({
    HRM2130_R01Filter: { ...defaultHRM2130_R01Filter },
    HRM2130_R01PastFilter: { ...defaultHRM2130_R01Filter },
    /** 전달된 검색 필드만 현재 조건에 병합한다. */
    setHRM2130_R01Filter: (filter) => set(state => ({ HRM2130_R01Filter: { ...state.HRM2130_R01Filter, ...filter } })),
    /** 마지막 조회에 사용한 검색 조건을 부분 갱신한다. */
    setHRM2130_R01PastFilter: (filter) => set(state => ({ HRM2130_R01PastFilter: { ...state.HRM2130_R01PastFilter, ...filter } })),
    HRM2130_R01List: [],
    /** 서버 행의 누락된 화면 기본값과 그리드 식별·정렬 인덱스를 채워 목록으로 저장한다. */
    setHRM2130_R01List: (list) => set({
        HRM2130_R01List: list.map((item, index) => ({
            ...defaultHRM2130_R01Dto, ...item,
            // 이미 부여된 valueIndex는 유지하고, 서버 행처럼 값이 없으면 1부터 부여한다.
            valueIndex: item.valueIndex > 0 ? item.valueIndex : index + 1,
            orderIndex: item.orderIndex > 0 ? item.orderIndex : index + 1,
            orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
        })),
    }),
    /**
     * 신규 행에 고유 valueIndex와 기준 행의 정렬 인덱스를 부여한다.
     * index가 있으면 해당 행 다음에 삽입한다. 기준 행을 찾지 못하면 맨 앞에, index가 없으면 맨 뒤에 넣는다.
     */
    insertHRM2130_R01List: (data, index) => set(state => {
        const list = [...state.HRM2130_R01List];
        const target = list.find(item => item.valueIndex === index);
        const orderIndex = target?.orderIndex ?? 0;
        const orderIndexSeq = (target?.orderIndexSeq ?? 0) + 1;
        const maxIndex = Math.max(0, ...list.map(item => item.valueIndex));
        const newRows = data.map((item, offset) => ({
            ...defaultHRM2130_R01Dto, ...item,
            valueIndex: maxIndex + offset + 1,
            orderIndex,
            orderIndexSeq,
        }));
        // + 버튼에서 기준 행이 전달되면 해당 행 바로 뒤에 삽입한다.
        if (index !== undefined) {
            list.splice(Math.max(0, list.findIndex(item => item.valueIndex === index) + 1), 0, ...newRows);
        } else {
            // 기준 행이 없으면 신규 행을 목록 끝에 추가한다.
            list.push(...newRows);
        }
        return { HRM2130_R01List: list };
    }),
    /** valueIndex로 신규 행을 제거하고 선택·삭제 목록에서도 같은 식별값을 지운다. */
    removeHRM2130_R01List: (index) => set(state => ({
        HRM2130_R01List: state.HRM2130_R01List.filter(item => item.valueIndex !== index),
        // 목록에 숫자와 문자열 식별값이 섞일 수 있어 양쪽을 문자열로 맞춰 비교한다.
        selectedHRM2130_R01List: state.selectedHRM2130_R01List.filter(id => String(id) !== String(index)),
        deletedHRM2130_R01List: state.deletedHRM2130_R01List.filter(id => String(id) !== String(index)),
    })),
    /** 해당 valueIndex 행을 교체한다. 음수 순서는 경고 후 0으로 보정한 목록을 반환한다. */
    updateHRM2130_R01List: (index, data) => set(state => {
        const updated = { ...data };
        // 음수 순서를 그대로 Store에 보관하지 않는다.
        if (updated.sortSeq < 0) {
            showWarning('순서는 0보다 작을 수 없습니다.');
            updated.sortSeq = 0;
        }
        return { HRM2130_R01List: state.HRM2130_R01List.map(item => item.valueIndex === index ? updated : item) };
    }),
    selectedHRM2130_R01List: [],
    /** 선택 식별값 전체를 전달값으로 교체한다. */
    setSelectedHRM2130_R01List: (ids) => set({ selectedHRM2130_R01List: ids }),
    /** 새 선택 식별값을 중복 없이 합친다. */
    addSelectedHRM2130_R01List: (ids) => set(state => ({
        selectedHRM2130_R01List: Array.from(new Set([...state.selectedHRM2130_R01List, ...ids])),
    })),
    /** ids가 있으면 해당 선택만 제거하고, 없으면 전체 선택을 해제한다. */
    removeSelectedHRM2130_R01List: (ids) => set(state => ({
        selectedHRM2130_R01List: state.selectedHRM2130_R01List.filter(id => ids ? !ids.includes(id) : false),
    })),
    /** 현재 선택 목록을 빈 배열로 초기화한다. */
    initSelectedHRM2130_R01List: () => set({ selectedHRM2130_R01List: [] }),
    /** 현재 목록의 모든 valueIndex를 문자열 식별값으로 선택한다. */
    allSelectedHRM2130_R01List: () => set(state => ({
        selectedHRM2130_R01List: state.HRM2130_R01List.map(item => String(item.valueIndex)),
    })),
    deletedHRM2130_R01List: [],
    /** 저장 시 삭제할 기존 행의 식별값을 중복 없이 추가한다. */
    addDeletedHRM2130_R01List: (ids) => set(state => ({
        deletedHRM2130_R01List: Array.from(new Set([...state.deletedHRM2130_R01List, ...ids])),
    })),
    /** ids가 있으면 지정된 삭제 표시만, 없으면 삭제 표시 전체를 지운다. */
    removeDeletedHRM2130_R01List: (ids) => set(state => ({
        deletedHRM2130_R01List: state.deletedHRM2130_R01List.filter(id => ids ? !ids.includes(id) : false),
    })),
    originalHRM2130_R01List: [],
    /** 서버 조회 원본에 안정적인 인덱스를 부여해 변경 비교용 목록으로 저장한다. */
    setOriginalHRM2130_R01List: (list) => set({
        originalHRM2130_R01List: list.map((item, index) => ({
            ...defaultHRM2130_R01Dto, ...item,
            valueIndex: index + 1, orderIndex: index + 1, orderIndexSeq: 0,
        })),
    }),
    columnState: null,
    /** 그리드 컬럼 순서·너비·표시 상태를 저장한다. */
    setColumnState: (columnState) => set({ columnState }),
    scrollTop: null,
    /** 조회 또는 탭 복귀에 사용할 그리드 스크롤 위치를 저장한다. */
    setScrollTop: (scrollTop) => set({ scrollTop }),
    allCount: 0,
    /** 현재 검색 조건에 맞는 서버 전체 건수를 저장한다. */
    setAllCount: (allCount) => set({ allCount }),
    /** 필터·목록·선택·삭제·원본·그리드 상태를 초기화한다. 근태구분 선택지는 공통 Store에서 관리한다. */
    initData: () => set({
        HRM2130_R01Filter: { ...defaultHRM2130_R01Filter },
        HRM2130_R01PastFilter: { ...defaultHRM2130_R01Filter },
        HRM2130_R01List: [],
        selectedHRM2130_R01List: [],
        deletedHRM2130_R01List: [],
        originalHRM2130_R01List: [],
        columnState: null,
        allCount: 0,
        scrollTop: null,
    }),
}));
